import classNames from 'classnames';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { SponsorUrl } from 'components-modules/rss-feed/sponsor-url.js';
import { toZonedTime as utcToZonedTime } from 'date-fns-tz/toZonedTime';
import { parse } from 'date-fns/parse';
import { useEffect, useState } from 'react';
import { IModule } from 'redux-state/layout/types.js';
import { TIMEZONE, formatDate } from 'utilities/date.js';
import { makeRequest } from 'utilities/fetch/api-gateway.js';

// RssFeed isn't prefixed with the API_URL from the .env configs e.g. /api or /publicData/webdata
// Therefore, when in dev we need to prefix it with the express server address, but in UAT and Prod we prefix it with nothing

export interface IRssFeed {
  title: string;
  link: string;
  desc: string;
  date?: string;
}

export interface IRssFeedProps {
  rssDataUrl: string;
  attribution?: string;
  sponsorImage?: string;
  sponsorUrl?: string;
  attributionUrl?: string;
  attributionUrlLabel?: string;
  articleCount?: number;
  dateFormat?: string;
}

interface IState {
  rssFeed: IRssFeed[];
  error: boolean;
  isLoading: boolean;
}

export const RssFeed = ({ config, isDrawer, modulePath }: IModule<IRssFeedProps>) => {
  const {
    rssDataUrl,
    sponsorImage,
    sponsorUrl,
    attribution,
    attributionUrl,
    attributionUrlLabel,
    articleCount,
    dateFormat,
  } = config;

  const [state, updateState] = useState<IState>({ rssFeed: [], error: false, isLoading: true });

  useEffect(() => {
    let cancel = false;

    const fetchRssFeed = async () => {
      try {
        const response = await makeRequest<IRssFeed[]>({ path: rssDataUrl, apiPrefix: false });

        if (!cancel) {
          updateState({ ...state, rssFeed: (response ?? []).slice(0, articleCount), error: false, isLoading: false });
        }
      } catch (_error) {
        if (!cancel) {
          updateState({ ...state, rssFeed: [], error: true, isLoading: false });
        }
      }
    };

    fetchRssFeed();

    return () => {
      cancel = true;
    };
  }, [rssDataUrl]);

  if (!rssDataUrl) {
    return null;
  }

  const { rssFeed, isLoading, error } = state;

  return (
    <Tile config={config} dataModule="RssFeed" classes={classNames('RssFeed', { 'RssFeed--hasImage': sponsorImage })}>
      <div className="Tile-body RssFeed-body">
        <TileBodyHeaderV2 isDrawer={isDrawer} modulePath={modulePath} config={config} classes="u-mB-responsive" />

        {isLoading && <p>Loading...</p>}

        {error && !isLoading && <p>Something went wrong.</p>}

        {rssFeed && (
          <ul className="RssFeed-list">
            {rssFeed.map(({ date, desc, link, title }, index) => {
              let parsed = date && parse(date, dateFormat || 'dd-MM-yyyy', utcToZonedTime(new Date(), TIMEZONE));
              parsed =
                !parsed || parsed?.toString() === 'Invalid Date' ? date && utcToZonedTime(date, TIMEZONE) : parsed;
              const displayDate = parsed && formatDate(parsed, 'EEEE, d MMMM yyyy');

              return (
                <li className="RssFeed-item" key={`${link}-${index}`}>
                  <LinkFactoryReactRouter
                    href={link}
                    className="RssFeed-link u-linkClean"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h3 className="h2 u-mT-0 u-mB-0">{title}</h3>
                  </LinkFactoryReactRouter>

                  {displayDate && <small className="fineprint u-block u-textGrey u-mT-xs u-mB-xs">{displayDate}</small>}

                  <p className="u-mT-xs u-mB-0">{desc}</p>
                </li>
              );
            })}
          </ul>
        )}

        {attribution && (
          <small className="fineprint u-block u-textGrey u-mT-xs u-mB-0">
            {attribution}
            {attributionUrlLabel && attributionUrl && (
              <LinkFactoryReactRouter
                href={attributionUrl}
                className="Link u-linkClean"
                target="_blank"
                rel="noopener noreferrer"
              >
                {attributionUrlLabel}
              </LinkFactoryReactRouter>
            )}
          </small>
        )}
      </div>

      {sponsorImage && (
        <div className="RssFeed-image Tile-body Tile-body--noPadding">
          <SponsorUrl sponsorUrl={sponsorUrl}>
            <img src={sponsorImage} className="u-block" width="100%" />
          </SponsorUrl>
        </div>
      )}
    </Tile>
  );
};
